import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { NavLink as Link } from 'react-router-dom';
import { ReactComponent as MailIcon } from '../../../assets/svg/mail.svg';
import { ReactComponent as PhoneIcon } from '../../../assets/svg/phone.svg';
import { ReactComponent as VisibilityIcon } from '../../../assets/svg/visibility.svg';
import * as peopleDb from '../../../shared/db/people-db';
import LocalTile from '../devices/DeviceTabPageEdit/LocalTile';

import EditIcon from '../../../assets/svg/edit.svg';

import moment from 'moment';
import Select from 'react-select';
import { logout as logoutAPI } from '../../../api';
import instance from '../../../api/instance';
import HistoryIcon from '../../../assets/svg/history_24px.svg';
import C, { ACL, IS_MOBILE_APP, UserCan } from '../../../shared/constants';
import { clientDb, fbapp } from '../../../shared/firebase';
import { resetRememberMeIfExists } from "../../../shared/helpers";
import { ItemType, MenuPinType } from '../../../shared/interfaces';
import { localStorage } from '../../../shared/storage';
import { AuthAC } from '../../../stores/reducers/auth-reducer';
import { history } from '../../../stores/store';
import DashboardBlock from '../../DashboardBlock';
import Dialog, { DialogConfigSetter } from '../../Dialog';
import { AllUsersFeatures } from "../../elements/AllUserFeatures";
import { ExtraInfo } from '../../elements/ExtraInfo/extra-info';
import { Col, Row } from '../../elements/flex';
import { FingerprintHelper } from '../../forms/LoginForm/FingerPrintHelper';
import MinimalTile from '../../general/MinimalTile';
import { customSelectStyles } from '../../general/selections-grid/constants';
import ModalHeader from '../../menus/modal/modal-header';
import Logout from '../../SVG-components/logout';
import { FormatAddress } from '../devices/device-list';
import { formatWhenLast } from '../devices/device-page';
import { DefaultSettingsTile } from './DefaultSettingsTile';
import GroupsTile from "./GroupsTile/GroupsTile";
import styles from './PersonTabPage.module.scss';
import { IFullProps, mapStateToProps } from './PersonTabPage.types';
import ResetPassword from './ResetPassword';
import RFIDTagsTile from "./RFIDTagsTile";
import { VisibleDevicesTile } from './VisibleDevicesTile';
import VisibleTagsTile from './VisibleTagsTile/VisibleTagsTile';
import ModalImage from '../../ModalImage';

const fingerprintHelper = new FingerprintHelper();

const PersonTabPage: React.FC<IFullProps> = (props) => {
  const {
    assignedDevice,
    assignedDeviceLastPing,
    person,
    currentLabels,
    userCanDo,
    authUser,
    username,
    itemTags,
    devicesDetails,
    tags,
    config,
    dispatch,
    match,
  } = props;

  const { params: { id: personId } } = match;

  const [selectedTime, setSelectedTime] = useState(3600000);
  const [hasFingerprint, setHasFingerprint] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [RFIDTag, setRFIDTag] = useState(null);
  const [personIsSubAdmin, setPersonIsSubAdmin] = useState(false);
  const [personIsAdmin, setPersonIsAdmin] = useState(false);
  const [personCanViewSafetyTeam, setPersonCanViewSafetyTeam] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [personGroups, setPersonGroups] = useState({});
  const [menuPinSettings, setMenuPinSettings] = useState({});
  const [imageSrc, setImageSrc] = useState(null);

  const dialogRef = useRef<DialogConfigSetter>(null);

  const setupDialog = useCallback((callBack: () => DialogConfigSetter) => {
    dialogRef.current = callBack();
  }, []);

  const checkFingerprint = useCallback(() => {
    if (personId !== authUser.uid) return;
    fingerprintHelper
      .isUserHaveFingerPrint()
      .then((res) => setHasFingerprint(res.has_fingerprint));
  }, [personId, authUser.uid]);

  const getRFIDTag = useCallback(async () => {
    try {
      const response: { data } = await instance.get(`api/rfid-tag/user-tag?user_id=${personId}`);
      setRFIDTag(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [personId]);

  const checkUserStatus = useCallback(async () => {
    const userId = person.id;
    try {
      const response: { data: { is_disabled: number, message: string } } = await instance.get(`api/user/check-user-status?firebase_uid=${userId}`);
      setIsDisabled(!!response.data.is_disabled);
    } catch (error) {
      console.error(error);
    }
  }, [person?.id]);

  useEffect(() => {
    if (!person) return;

    const load = async () => {
      const personSubAdmin = (await clientDb().child(`/acl/can/sub-admin/${person.id}`).once('value')).val();
      const personAdmin = (await clientDb().child(`/acl/can/do-anything/${person.id}`).once('value')).val();
      const safetyTeamKey = (UserCan as any).VIEW_SAFETY_TEAM || 'view-safety-team';
      const personSafetyTeam = (await clientDb().child(`/acl/can/${safetyTeamKey}/${person.id}`).once('value')).val();

      const fetchedPersonGroups = (await clientDb().child(`/people/people-details/${person.id}/groups`).once('value')).val() || [];
      const logoutTime = (await clientDb().child(`/people/people-details/${person.id}/logoutTime`).once('value')).val() || 3600000;
      const groupsRaw = (await clientDb().child(`/groups`).once('value')).val();
      const resolvedGroups = groupsRaw
        ? Object.keys(groupsRaw)
            .filter((group) => Object.keys(fetchedPersonGroups).includes(group))
            .map((group) => ({ group, name: groupsRaw[group].name }))
        : [];

      const fetchedMenuPinSettings = (await clientDb().child(`/people/people-details/${person.id}/menuPin`).once('value')).val();
      if (fetchedMenuPinSettings) {
        setMenuPinSettings(fetchedMenuPinSettings);
      }

      const adminToken = localStorage.get('login-init-be-token');
      const loginUser = localStorage.get('login-init-user');
      const rawSubUsers = loginUser['subUsers'];

      setPersonIsSubAdmin(!!personSubAdmin);
      setPersonIsAdmin(!!personAdmin);
      setPersonCanViewSafetyTeam(!!personSafetyTeam);
      setIsAdmin(!!adminToken[UserCan.DO_ANYTHING]);
      setIsSubAdmin(!!adminToken[UserCan.SUB_ADMIN]);
      setSubUsers(Array.isArray(rawSubUsers) ? rawSubUsers : []);
      setGroups(resolvedGroups);
      setPersonGroups(fetchedPersonGroups);
      setSelectedTime(moment.duration(logoutTime, 'milliseconds').asMilliseconds());

      checkUserStatus();
      getRFIDTag();

      if (IS_MOBILE_APP) checkFingerprint();
    };

    load();
  }, [person?.id]);

  const handleDisableAccount = async () => {
    const userId = person.id;
    try {
      const response: { data: { is_disabled: boolean, message: string } } = await instance.get(`api/user/toggle-user-status?firebase_uid=${userId}`);
      setIsDisabled(response.data.is_disabled);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleSubAdmin = async () => {
    const value = personIsSubAdmin ? null : true;
    setPersonIsSubAdmin(!!value);

    await clientDb().update({
      [`acl/can/sub-admin/${person.id}`]: value,
    });

    if (Object.keys(personGroups).length) {
      const values = {};
      for (const group of Object.keys(personGroups)) {
        values[`groups/${group}/sub-admins/${person.id}`] = value ? true : null;
        values[`groups/${group}/sub-users/${person.id}`] = value ? null : true;
      }
      await clientDb().update(values);
    }
  };

  const handleToggleSafetyTeamAccess = async () => {
    const value = personCanViewSafetyTeam ? null : true;
    const safetyTeamKey = (UserCan as any).VIEW_SAFETY_TEAM || 'view-safety-team';
    setPersonCanViewSafetyTeam(!!value);
    await clientDb().update({
      [`acl/can/${safetyTeamKey}/${person.id}`]: value,
    });
  };

  const closeImageHandler = (e) => {
    e.stopPropagation();
    setImageSrc(null);
  };

  const handleImageClick = (src) => {
    setImageSrc(src);
  };

  if (!person) return null;

  const hasTags = !!Object.keys(itemTags).length;

  const ago = formatWhenLast(
    assignedDevice ? assignedDeviceLastPing?.time : undefined
  );

  const isAdminPage = ACL.check(UserCan.DO_ANYTHING, userCanDo) && personId === authUser.uid;
  const isAdminOrSubAdmin = ACL.check(UserCan.DO_ANYTHING, userCanDo) || ACL.check(UserCan.SUB_ADMIN, userCanDo);
  const isDriver = ACL.checkDriver(UserCan.ASSIGN_DRIVER, userCanDo);

  const logout = async (e) => {
    e.preventDefault();
    await logoutAPI();
    document.location.href = '/';
    resetRememberMeIfExists();
    fbapp.auth().signOut();
  };

  const removeFingerprint = () => {
    const userId = authUser.uid;
    const dialog = dialogRef.current;
    dialog?.({
      type: 'CONFIRM',
      title: 'Delete Fingerprint?',
      body: 'You are about to delete a fingerprint. Are you sure?',
    }).then((confirmed) => {
      confirmed &&
        fingerprintHelper
          .deleteFingerPrintToken({ username, userId })
          .then(({ data }) => {
            dialog?.({
              type: 'NOTIFICATION',
              title: 'Result',
              body: data.status,
            });
            setHasFingerprint((prev) => !prev);
          })
          .catch((err) => console.log(err));
    });
  };

  const setNewTime = async (selectedLogoutTime) => {
    const { value } = selectedLogoutTime;
    const newValue = value * 60 * 60 * 1000;
    setSelectedTime(newValue);
    await clientDb().update({
      [`people/people-details/${person.id}/logoutTime`]: newValue,
    });
    dispatch(AuthAC.setLogoutTime(newValue));
  };

  const formatTimeForSelect = () => {
    const hours = Math.floor(selectedTime / 3600000);
    return hours === 1 ? `${hours} hour` : `${hours} hours`;
  };

  const onDisableUser = (id) => () => {
    peopleDb.updatePersonDetails(authUser)(id, { 'disabled': true });
  };

  const onEnableUser = (id) => () => {
    peopleDb.updatePersonDetails(authUser)(id, { 'disabled': false });
  };

  const optionsForLogoutSelector = Array(24)
    .fill('')
    .map((_, i) => ({
      value: i + 1,
      label: `${i + 1} ${i === 0 ? 'hour' : 'hours'}`,
    }));

  const selectTimePlaceholder = formatTimeForSelect();

  return (
    <DashboardBlock>
      <ModalHeader
        title={<span style={{ fontSize: '12px' }}>{person ? person.displayName : ''}</span>}
        action={
          !isDriver && {
            icon: EditIcon,
            title: 'Edit Person Details',
            click: () => history.push(`/person/${personId}/edit`),
          }
        }
        role={isDriver}
      />

      <div>
        {/*<div style={{ display: 'flex', marginBottom: 10 }}>*/}
        {/*  <MinimalTile*/}
        {/*    style={{*/}
        {/*      display: 'flex',*/}
        {/*      flex: 1,*/}
        {/*      flexDirection: 'row',*/}
        {/*      gap: '12px',*/}
        {/*      fontSize: 16,*/}
        {/*      lineHeight: '15px',*/}
        {/*      position: 'relative',*/}
        {/*      marginLeft: 5,*/}
        {/*      padding: 12,*/}

        {/*    }}*/}
        {/*  >*/}
        {/*      <div*/}
        {/*        style={{*/}
        {/*          width: 64,*/}
        {/*          height: 64,*/}
        {/*          borderRadius: 70,*/}
        {/*          backgroundImage: `url(${person.photoURL})`,*/}
        {/*          backgroundSize: 'cover',*/}
        {/*          border: `2px solid ${C.mediumGray}`,*/}
        {/*        }}*/}
        {/*      />*/}
        {/*      <section className={styles.lastPingInfo}>*/}
        {/*        {assignedDeviceLastPing && (*/}
        {/*          <FormatAddress*/}
        {/*            style={{*/}
        {/*              alignSelf: 'stretch',*/}
        {/*              paddingTop: 5,*/}
        {/*              textAlign: 'center',*/}
        {/*              lineHeight: '18px',*/}
        {/*              fontWeight: 'lighter',*/}
        {/*            }}*/}
        {/*            address={assignedDeviceLastPing.address}*/}
        {/*          />*/}
        {/*        )}*/}
        {/*      {(assignedDevice && assignedDevice.id !== '-1') && (*/}
        {/*        <Col align='flex-start' justify='space-between'>*/}
        {/*          <span className={styles.assignedDevice}>Assigned Device</span>*/}
        {/*          <span className={styles.assignedDeviceName}>{assignedDevice.name}</span>*/}
        {/*        </Col>*/}
        {/*      )}*/}
        {/*      <span*/}
        {/*        style={{*/}
        {/*          fontSize: 10,*/}
        {/*        }}*/}
        {/*        className={styles.lastSeen}*/}
        {/*      >*/}
        {/*        <VisibilityIcon /> Last*/}
        {/*        Seen: <span className={styles.value}>{ago || 'N/A'}</span>*/}
        {/*      </span>*/}
        {/*    </section>*/}
        {/*  </MinimalTile>*/}
        {/*</div>*/}

        <MinimalTile className={styles.infoTile}>
          <Row align='center' justify='space-between' className={styles.infoRow}>
            <span>
              <MailIcon /> &nbsp; Email
            </span>
            {person.email || 'none'}
          </Row>
        </MinimalTile>

        {/*HIDDEN*/}
        {/*{isAdmin && <GroupsTile*/}
        {/*  groups={groups}*/}
        {/*  editUrl={`/person/${personId}/groups`}*/}
        {/*/>}*/}

        <VisibleTagsTile
          personId={personId}
          userCanDo={userCanDo}
          devicesDetails={devicesDetails}
          tags={tags}
          editUrl={`/person/${personId}/visible-tags`}
          isPinned={false}
          property={MenuPinType.VISIBLE_TAGS}
          showPin={false}
        />

        {!config.strictAccess ? null : (
          <VisibleDevicesTile
            personId={personId}
            userCanDo={userCanDo}
            devicesDetails={devicesDetails}
            tags={tags}
            editUrl={`/person/${personId}/visible-devices`}
            isPinned={false}
            property={MenuPinType.VISIBLE_DEVICES}
            showPin={false}
          />
        )}

        {/* Info Section - Commented out */}
        {/* <ExtraInfo
          editUrl={`/${ItemType.person}/${personId}/add/extra-info`}
          labels={currentLabels}
          role={isDriver}
          showPin={false}
          title={null}
          property={MenuPinType.EXTRA_INFO}
          imageClick={handleImageClick}
          isPinned={true}
          itemType={ItemType.person}
          itemId={personId}
        /> */}

        {/* RFID Section - Commented out */}
        {/* {(isAdminOrSubAdmin || (isSubAdmin && subUsers.includes(personId))) && (
          <RFIDTagsTile
            editUrl={`/person/${personId}/rfid-tags`}
            personId={personId}
            RFIDTag={RFIDTag}
            isPinned={false}
            showPin={false}
          />
        )} */}

        {isAdminPage && (
          <DefaultSettingsTile personId={personId} userCanDo={userCanDo} editUrl={`/person/${personId}/default-settings`} />
        )}

        {isAdminPage && <AllUsersFeatures isPinned={false} />}

        {personId === authUser.uid && <ResetPassword />}

        {personId === authUser.uid && (
          <LocalTile title='Log Out Time' leftIcon={null} style={{ padding: '8px 12px 12px' } as React.CSSProperties}>
            <div style={{ margin: '10px 0' }}>
              <Select
                key='logout'
                onChange={(e) => setNewTime(e)}
                value={selectedTime}
                placeholder={selectTimePlaceholder}
                styles={customSelectStyles}
                options={optionsForLogoutSelector}
              />
            </div>
          </LocalTile>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
          {isAdmin && !personIsAdmin && (
            <button
              className={classNames('btn', 'btn-primary')}
              style={{ width: '100%' }}
              onClick={handleToggleSubAdmin}
            >
              {personIsSubAdmin ? 'Remove Sub Admin' : 'Make Sub Admin'}
            </button>
          )}

          {isAdmin && !personIsAdmin && (
            <button
              className={classNames('btn', 'btn-primary')}
              style={{ width: '100%' }}
              onClick={handleToggleSafetyTeamAccess}
            >
              {personCanViewSafetyTeam ? 'Revoke Safety Team Access' : 'Enable Safety Team Access'}
            </button>
          )}
        </div>

        <div
          style={{
            margin: '12px 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {personId === authUser.uid && (
            <Link
              onClick={logout}
              to='/login'
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                marginTop: 10,
              }}
              className={styles.logoutBtn}
            >
              <Logout
                style={{
                  height: 30,
                  width: 30,
                  fill: `${C.primaryText}`,
                  marginRight: 10,
                }}
              />
              <p>Log Out</p>
            </Link>
          )}
          {personId === authUser.uid && IS_MOBILE_APP && hasFingerprint && (
            <button className='btn btn-primary' onClick={removeFingerprint}>
              Delete Fingerprint
            </button>
          )}
          {personId !== authUser.uid && isAdminOrSubAdmin && !person.disabled && (
            <button
              className={classNames('btn', 'btn-primary')}
              style={{ width: '100%' }}
              onClick={handleDisableAccount}
            >
              {isDisabled ? 'Enable this account' : 'Disable this account'}
            </button>
          )}
          {personId !== authUser.uid && isAdminOrSubAdmin && person.disabled && (
            <button
              className='btn btn-primary'
              style={{ width: '100%' }}
              onClick={onEnableUser(personId)}
            >
              Enable this account
            </button>
          )}

          <Dialog setupConfig={setupDialog} />
        </div>
      </div>
      {imageSrc && (
        <ModalImage
          closeImageHandler={closeImageHandler}
          imageSrc={imageSrc}
        />
      )}
    </DashboardBlock>
  );
};

export default connect(mapStateToProps)(PersonTabPage);
